import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inquiryApi } from './inquiry.api.ts';
import { inquiryKeys } from './inquiry.keys.ts';
import type {
  AnswerInquiryInput,
  CreateInquiryInput,
  Inquiry,
  UpdateInquiryInput,
} from '../inquiry.type.ts';

/** 문의 생성. 성공 시 목록 무효화. */
export function useCreateInquiry() {
  const qc = useQueryClient();
  return useMutation<Inquiry, Error, CreateInquiryInput>({
    // InquiryForm(onError) · InquiryDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (input) => inquiryApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inquiryKeys.lists() });
    },
  });
}

/** 문의 수정. 성공 시 목록 + 해당 상세 무효화. */
export function useUpdateInquiry(id: string | number) {
  const qc = useQueryClient();
  return useMutation<Inquiry, Error, UpdateInquiryInput>({
    // InquiryForm(onError) · InquiryDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (input) => inquiryApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inquiryKeys.lists() });
      qc.invalidateQueries({ queryKey: inquiryKeys.detail(id) });
    },
  });
}

/** 문의 삭제. 성공 시 목록 무효화 + 상세 캐시 제거. */
export function useDeleteInquiry() {
  const qc = useQueryClient();
  return useMutation<void, Error, string | number>({
    // InquiryForm(onError) · InquiryDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (id) => inquiryApi.remove(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: inquiryKeys.lists() });
      qc.invalidateQueries({ queryKey: inquiryKeys.userMenuNotification() });
      qc.removeQueries({ queryKey: inquiryKeys.detail(id) });
    },
  });
}

/** 사용자 답변 확인 처리. 성공 시 목록과 미확인 답변 여부를 갱신. */
export function useMarkInquiryRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string | number>({
    mutationFn: (id) => inquiryApi.markInquiryRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: inquiryKeys.lists() });
      void qc.invalidateQueries({
        queryKey: inquiryKeys.userMenuNotification(),
      });
    },
  });
}

/** 운영자 답변 등록. 성공 시 목록 + 상세 무효화. */
export function useAnswerInquiry(id: string | number) {
  const qc = useQueryClient();
  return useMutation<Inquiry, Error, AnswerInquiryInput>({
    // InquiryForm(onError) · InquiryDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (input) => inquiryApi.answer(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inquiryKeys.lists() });
      qc.invalidateQueries({ queryKey: inquiryKeys.detail(id) });
    },
  });
}
